/* Poll wording shared by thread labels and the chat list preview. */

import { isLocalUser } from '../../Helpers/Chat/User';
import { i18n } from '../../localization/i18n';
import { getOtherPersonName } from './pollPeople';
import { POLL_CLOSE_REASON, POLL_EVENT } from './pollRules';

// Placeholder for the name, so the sentence can be split around it for bolding.
const NAME_TOKEN = '\u2063';

const LABEL_KEYS = {
    [POLL_EVENT.CREATED]: ['LABEL_CREATED_YOU', 'LABEL_CREATED'],
    [POLL_EVENT.VOTED]: ['LABEL_VOTED_YOU', 'LABEL_VOTED'],
    [POLL_EVENT.VOTE_CHANGED]: ['LABEL_VOTE_CHANGED_YOU', 'LABEL_VOTE_CHANGED'],
    [POLL_EVENT.CLOSED]: ['LABEL_CLOSED_YOU', 'LABEL_CLOSED'],
    [POLL_EVENT.RESHARED]: ['LABEL_RESHARED_YOU', 'LABEL_RESHARED'],
};

// Read on call, not at module load: i18n resolves the active language at render time.
const pollLabel = (key, params) => i18n.t(`chat.poll.${key}`, params);

/**
 * Activity label of a poll, split around the actor so the name can be styled.
 *
 * @param {object} activity
 * @param {string} activity.event Poll event.
 * @param {string} activity.reason manual or deadline, for a closed event.
 * @param {boolean} activity.isActorMe Whether the viewer acted.
 * @param {string} activity.actorName Display name of the actor.
 * @returns {{before: string, name: string, after: string}|null} Null for an event with no label.
 */
export const getPollActivityParts = ({ event, reason, isActorMe = false, actorName = '' }) => {
    if (event === POLL_EVENT.CLOSED && reason === POLL_CLOSE_REASON.DEADLINE) {
        return { before: pollLabel('LABEL_CLOSED_DEADLINE'), name: '', after: '' };
    }
    const keys = LABEL_KEYS[event];
    if (!keys) return null;

    const name = isActorMe ? i18n.t('chat.detail.YOU') : actorName;
    const sentence = pollLabel(isActorMe ? keys[0] : keys[1], { name: NAME_TOKEN });
    const [before = '', after = ''] = sentence.split(NAME_TOKEN);
    return { before, name, after };
};

/**
 * Activity label of a poll as plain text.
 *
 * @param {object} activity Same fields as getPollActivityParts.
 * @returns {string} The label, or an empty string for an event with no label.
 */
export const getPollActivityText = activity => {
    const parts = getPollActivityParts(activity);
    return parts ? `${parts.before}${parts.name}${parts.after}` : '';
};

/**
 * Chat list preview of a poll message: the creation with its question, or the latest activity.
 *
 * @param {object} preview
 * @param {string} preview.question Poll question.
 * @param {string} preview.event pollEvent of the recent chat meta, empty right after creation.
 * @param {string} preview.reason pollReason of the recent chat meta, for a closed event.
 * @param {boolean} preview.isActorMe Whether the viewer acted.
 * @param {string} preview.actorName Display name of the actor.
 * @returns {string} Preview text.
 */
export const getPollPreviewText = ({ question = '', event = '', reason = '', isActorMe = false, actorName = '' }) => {
    if (event && event !== POLL_EVENT.CREATED) {
        const text = getPollActivityText({ event, reason, isActorMe, actorName });
        if (text) return text;
    }
    const name = isActorMe ? i18n.t('chat.detail.YOU') : actorName;
    return pollLabel(isActorMe ? 'PREVIEW_CREATED_YOU' : 'PREVIEW_CREATED', { name, question });
};

/**
 * One-line text of a poll message, for the chat list and the pinned and starred lists.
 *
 * @param {object} source
 * @param {object} source.poll `msgBody.poll` of the poll message.
 * @param {string} source.publisherId Sender of the poll message.
 * @param {string} source.pollEvent Latest poll event from the recent chat meta, if any.
 * @param {string} source.pollActor Actor of that event.
 * @param {string} source.pollReason Reason of that event, for a closed poll.
 * @returns {string} Preview text.
 */
export const getPollMessagePreview = ({ poll, publisherId = '', pollEvent = '', pollActor = '', pollReason = '' }) => {
    const actor = pollEvent ? pollActor : poll?.createdBy || publisherId;
    return getPollPreviewText({
        question: poll?.question || '',
        event: pollEvent,
        reason: pollReason,
        isActorMe: isLocalUser(actor),
        actorName: getOtherPersonName(actor),
    });
};

/**
 * A counted phrase in the plural form the active language needs, e.g. "1 vote", "5 votes".
 *
 * @param {string} key Base key; its forms are `${key}_one`, `_few`, `_many`, `_two`, `_zero`, `_other`.
 * @param {number} count Number that picks the form.
 * @param {object} params Other placeholders of the phrase.
 * @returns {string} The phrase.
 */
export const getPollCountText = (key, count, params = {}) => {
    const category = new Intl.PluralRules(i18n.language).select(count);
    const path = `chat.poll.${key}_${category}`;
    const text = i18n.t(path, { count, ...params });
    // A language without that form falls back to the general one.
    return text === path ? pollLabel(`${key}_other`, { count, ...params }) : text;
};

/**
 * Share of the voters for an option, as the cards and the poll pop-up show it.
 *
 * @param {number} share Whole percent.
 * @returns {string} The share, e.g. "63%".
 */
export const getVoteShareText = share => pollLabel('SHARE', { share });

/**
 * "Alpha, Kim Le and 4 more" for the voters of an option.
 *
 * @param {string[]} names Names of the first voters, earliest first.
 * @param {number} total Voters of the option.
 * @returns {string} The summary.
 */
export const getVoterNamesText = (names, total) => {
    const shown = names.slice(0, 2);
    const rest = Math.max(total - shown.length, 0);
    if (rest > 0) return pollLabel('VOTERS_MORE', { names: shown.join(pollLabel('NAME_SEPARATOR')), count: rest });
    if (shown.length === 2) return pollLabel('VOTERS_TWO', { first: shown[0], second: shown[1] });
    return shown[0] || '';
};
